/**
 * SeoTypePage.duck.js
 *
 * Data layer for NATIONAL trailer-TYPE hub pages (e.g. /dump-trailer-rental).
 *
 * Unlike the city hub (SeoCategoryPage), which filters listings by a saved
 * geographic `bounds` search, a type hub filters by `pub_trailerType` with NO
 * geo bounds, so it shows the strongest national inventory for that type. The
 * content (H1, editorial, FAQ, schema) comes from the in-code TYPE_HUBS map in
 * util/typeSeoContent.js, so this duck only needs to load listings.
 *
 * SSR: loadData runs on the server so listings are present in the initial HTML
 * for crawlers. A listings failure must NEVER blank the page (the in-code
 * content still renders).
 */

import { createImageVariantConfig } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { getTypeHubBySlug } from '../../util/typeSeoContent';

// How many listings to pull for the national hub grid.
const HUB_LISTING_LIMIT = 24;

// ================ Action types ================ //

export const FETCH_TYPE_LISTINGS_REQUEST = 'app/SeoTypePage/FETCH_TYPE_LISTINGS_REQUEST';
export const FETCH_TYPE_LISTINGS_SUCCESS = 'app/SeoTypePage/FETCH_TYPE_LISTINGS_SUCCESS';
export const FETCH_TYPE_LISTINGS_ERROR = 'app/SeoTypePage/FETCH_TYPE_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  currentTypeSlug: null,
  typeResultIds: [],
  fetchInProgress: false,
  fetchError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_TYPE_LISTINGS_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null, currentTypeSlug: payload?.slug || null };
    case FETCH_TYPE_LISTINGS_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        typeResultIds: (payload?.data?.data || []).map(l => l?.id) || [],
      };
    case FETCH_TYPE_LISTINGS_ERROR:
      // Keep whatever we had; content still renders.
      return { ...state, fetchInProgress: false, fetchError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchTypeListingsRequest = slug => ({ type: FETCH_TYPE_LISTINGS_REQUEST, payload: { slug } });
export const fetchTypeListingsSuccess = response => ({ type: FETCH_TYPE_LISTINGS_SUCCESS, payload: response });
export const fetchTypeListingsError = e => ({ type: FETCH_TYPE_LISTINGS_ERROR, payload: e, error: true });

// ================ Selectors ================ //

export const getTypeResultIds = state => state.SeoTypePage?.typeResultIds || [];
export const getTypeListings = state => getListingsById(state, getTypeResultIds(state));

// ================ Thunks ================ //

/**
 * Query up to HUB_LISTING_LIMIT active listings nationally for a trailer type.
 * `pub_isInfoOnly: false` mirrors the city hub (only bookable listings).
 */
export const fetchTypeListings = (slug, config) => async (dispatch, getState, sdk) => {
  const hub = getTypeHubBySlug(slug);
  if (!hub) return null;
  dispatch(fetchTypeListingsRequest(slug));
  try {
    const response = await sdk.listings.query({
      pub_trailerType: hub.trailerType,
      pub_isInfoOnly: false,
      include: ['author', 'images'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.listing-card',
        'variants.listing-card-2x',
      ],
      ...createImageVariantConfig('listing-card', 400, 1),
      ...createImageVariantConfig('listing-card-2x', 800, 1),
      'limit.images': 1,
      perPage: HUB_LISTING_LIMIT,
    });
    const listingFields = config?.listing?.listingFields;
    dispatch(addMarketplaceEntities(response, { listingFields }));
    dispatch(fetchTypeListingsSuccess(response));
    return response;
  } catch (e) {
    // Best-effort: never blank the page on a listings failure.
    // eslint-disable-next-line no-console
    console.log('SeoTypePage: listings query failed (content preserved)', e && e.message);
    dispatch(fetchTypeListingsError(e));
    return null;
  }
};

// ================ SSR loadData ================ //

export const loadData = (params, search, config) => dispatch => {
  const slug = params?.seoTypeSlug || params?.slug || '';
  return dispatch(fetchTypeListings(slug, config));
};
